import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { styles } from './superadminStyles';

// Self-contained (unlike GisLayerMappings, which shares list state with the
// parent) since publishing a new version is a multipart/form-data POST that
// doesn't need to interact with any other console state.
const TermsManagement = () => {
    const [current, setCurrent] = useState(null);
    const [history, setHistory] = useState([]);
    const [content, setContent] = useState('');
    const [file, setFile] = useState(null);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    const [showHistory, setShowHistory] = useState(false);

    const loadHistory = useCallback(async () => {
        try {
            const res = await api.get('/terms/history');
            setHistory(res.data);
            const latest = res.data[0] || null;
            setCurrent(latest);
            setContent(latest ? latest.content : '');
        } catch (err) {
            setError('Failed to load Terms of Use history.');
        }
    }, []);

    useEffect(() => { loadHistory(); }, [loadHistory]);

    const handlePublish = async (e) => {
        e.preventDefault();
        setError('');
        if (!content.trim()) {
            setError('Terms of Use content cannot be empty.');
            return;
        }

        setSaving(true);
        try {
            const formData = new FormData();
            formData.append('content', content.trim());
            if (file) formData.append('file', file);

            const res = await api.put('/terms', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setCurrent(res.data);
            setHistory(prev => [res.data, ...prev]);
            setFile(null);
        } catch (err) {
            setError(err.response?.data?.msg || 'Failed to publish Terms of Use.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{ ...styles.card, gridColumn: '1 / -1' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <div>
                    <h2 style={{ marginTop: 0, marginBottom: '4px', color: '#1abc9c' }}>📄 Terms of Use</h2>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#bdc3c7' }}>
                        {current
                            ? `Currently published: version ${current.version}${current.updatedBy?.name ? ` (by ${current.updatedBy.name})` : ''}`
                            : 'No Terms of Use published yet — registration is blocked until one is published.'}
                    </p>
                </div>
                <button onClick={() => setShowHistory(v => !v)} style={{ ...styles.btn, background: '#3498db', padding: '8px 14px', fontSize: '0.85rem' }}>
                    {showHistory ? 'Hide History' : `History (${history.length})`}
                </button>
            </div>

            {showHistory && (
                <div style={{ maxHeight: '220px', overflowY: 'auto', marginBottom: '15px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                        <thead>
                            <tr style={{ background: '#2c3e50', color: '#bdc3c7', textAlign: 'left' }}>
                                <th style={{ padding: '8px' }}>Version</th>
                                <th style={{ padding: '8px' }}>Published</th>
                                <th style={{ padding: '8px' }}>By</th>
                                <th style={{ padding: '8px' }}>File</th>
                            </tr>
                        </thead>
                        <tbody>
                            {history.map(v => (
                                <tr key={v._id} style={{ borderBottom: '1px solid #7f8c8d' }}>
                                    <td style={{ padding: '8px', fontWeight: 'bold' }}>v{v.version}{v.isCurrent ? ' (current)' : ''}</td>
                                    <td style={{ padding: '8px' }}>{new Date(v.createdAt).toLocaleString()}</td>
                                    <td style={{ padding: '8px' }}>{v.updatedBy?.name || '—'}</td>
                                    <td style={{ padding: '8px' }}>
                                        {v.fileUrl
                                            ? <a href={`${process.env.REACT_APP_API_URL}${v.fileUrl}`} target="_blank" rel="noreferrer" style={{ color: '#1abc9c' }}>Download</a>
                                            : '—'}
                                    </td>
                                </tr>
                            ))}
                            {history.length === 0 && (
                                <tr><td colSpan="4" style={{ padding: '20px', textAlign: 'center', color: '#bdc3c7' }}>No versions published yet.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            <form onSubmit={handlePublish}>
                <div style={{ marginBottom: '15px' }}>
                    <label style={styles.label}>Terms of Use Content</label>
                    <textarea
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        style={{ ...styles.input, height: '220px', resize: 'vertical', fontFamily: 'inherit' }}
                        placeholder="Paste or write the full Terms of Use / Privacy Policy text here..."
                        required
                    />
                </div>

                <div style={{ marginBottom: '15px' }}>
                    <label style={styles.label}>Optional downloadable copy (PDF or DOCX)</label>
                    <input
                        type="file"
                        accept=".pdf,.docx"
                        onChange={(e) => setFile(e.target.files?.[0] || null)}
                        style={{ color: 'white' }}
                    />
                </div>

                {error && <div style={{ color: '#e74c3c', marginBottom: '15px', fontSize: '0.9rem' }}>{error}</div>}

                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button type="submit" disabled={saving} style={{ ...styles.btn, background: '#1abc9c', opacity: saving ? 0.6 : 1 }}>
                        {saving ? 'Publishing…' : 'Publish New Version'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default TermsManagement;
